/**
 * Query Parameter Synchronization Hook
 *
 * Provides bidirectional sync between URL query parameters and component state.
 * Handles debounced updates, type coercion, and navigation options.
 *
 * @module shared/hooks/routing/useQueryParamSync
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import { parseQueryParams, toSearchParams } from '@shared/utils/routing/routeParser';

/**
 * Type coercion schema for query parameters
 */
interface CoercionSchema {
  [key: string]: 'string' | 'number' | 'boolean' | 'string[]';
}

/**
 * Options for useQueryParamSync hook
 *
 * @template T - Type of query parameter object
 */
export interface UseQueryParamSyncOptions<T> {
  /**
   * Debounce delay in milliseconds (default: 500ms)
   */
  debounceMs?: number;

  /**
   * Use replace navigation instead of push (default: true)
   * - true: Replaces current history entry (prevents history pollution)
   * - false: Pushes new history entry (allows back navigation)
   */
  replace?: boolean;

  /**
   * Schema defining expected types for each parameter
   */
  schema?: Record<keyof T, 'string' | 'number' | 'boolean' | 'string[]'>;

  /**
   * Callback invoked after URL is synced with new params
   */
  onSync?: (params: T) => void;
}

/**
 * Return type for useQueryParamSync hook
 *
 * @template T - Type of query parameter object
 */
export interface UseQueryParamSyncReturn<T> {
  /**
   * Current parsed query parameters from URL
   */
  params: Partial<T>;

  /**
   * Update query parameters in URL (debounced)
   * Merges with existing params by default
   */
  setParams: (newParams: Partial<T>) => void;

  /**
   * Clear all query parameters from URL
   */
  clearParams: () => void;

  /**
   * Clear a specific query parameter from URL
   */
  clearParam: (key: keyof T) => void;
}

/**
 * Hook for bidirectional synchronization between URL query params and component state
 *
 * Features:
 * - Debounced URL updates to prevent history pollution
 * - Type-safe parameter handling with schema validation
 * - Support for primitives, arrays, and objects
 * - Replace vs push navigation options
 * - Automatic cleanup of undefined/null values
 *
 * @template T - Type of query parameter object
 * @param options - Configuration options
 * @returns Object with current params and update functions
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { params, setParams } = useQueryParamSync<{ search: string; page: number }>({
 *   schema: { search: 'string', page: 'number' }
 * });
 *
 * // Update params (debounced, merges with existing)
 * setParams({ search: 'test' });
 *
 * // Clear all params
 * clearParams();
 * ```
 *
 * @example
 * ```typescript
 * // With callback and custom debounce
 * const { params, setParams } = useQueryParamSync<OrganizationFilters>({
 *   debounceMs: 300,
 *   replace: true,
 *   schema: {
 *     search: 'string',
 *     status: 'string',
 *     tags: 'string[]',
 *     page: 'number'
 *   },
 *   onSync: (params) => {
 *     console.log('URL synced with params:', params);
 *   }
 * });
 * ```
 */
export function useQueryParamSync<T extends Record<string, any>>(
  options: UseQueryParamSyncOptions<T> = {}
): UseQueryParamSyncReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { debounceMs = 500, replace = true, schema, onSync } = options;

  /**
   * Parse current query parameters from URL
   */
  const getParams = useCallback((): Partial<T> => {
    const params = parseQueryParams<T>(searchParams.toString(), schema as CoercionSchema);
    return params;
  }, [searchParams, schema]);

  /**
   * Update URL with new query parameters (debounced)
   *
   * Merges new params with existing params and filters out undefined/null values.
   * Uses debouncing to prevent excessive history updates.
   */
  const setParams = useCallback(
    (newParams: Partial<T>) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Debounce URL update
      timeoutRef.current = setTimeout(() => {
        const current = getParams();
        const merged = { ...current, ...newParams };

        // Remove undefined/null/empty string values to clean up URL
        const filtered = Object.entries(merged).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            // For arrays, only include if non-empty
            if (Array.isArray(value)) {
              if (value.length > 0) {
                acc[key] = value;
              }
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {} as Record<string, any>);

        // Convert to URLSearchParams
        const newSearchParams = toSearchParams(filtered);

        // Update URL
        setSearchParams(newSearchParams, { replace });

        // Invoke callback if provided
        if (onSync) {
          onSync(filtered as T);
        }
      }, debounceMs);
    },
    [debounceMs, replace, getParams, setSearchParams, onSync]
  );

  /**
   * Clear all query parameters from URL
   */
  const clearParams = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSearchParams({}, { replace: true });
    if (onSync) {
      onSync({} as T);
    }
  }, [setSearchParams, onSync]);

  /**
   * Clear a specific query parameter from URL
   */
  const clearParam = useCallback(
    (key: keyof T) => {
      const current = getParams();
      const { [key]: removed, ...rest } = current;
      setParams(rest as Partial<T>);
    },
    [getParams, setParams]
  );

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    params: getParams(),
    setParams,
    clearParams,
    clearParam,
  };
}
